import {Switch} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {useLinePlotMaxRunsDynamicConfig} from '../../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {useRampFlagFullFidelityRunsPerChart} from '../../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {LabeledOptionBlock as LabeledOption} from '../../../elements/LabeledOptionBlock';
import {useSingleRunModeContext} from '../../../MultiRunWorkspace/SingleRunMode';
import {usePanelGroupingSettings} from '../../../PanelRunsLinePlot/RunsLinePlotContext/usePanelGroupingSettings';
import {useLinePlotSettings} from '../hooks';
import {FULL_FIDELITY_MODES, isPointVizFullFidelity} from '../types';
import {LinePlotExcludeOutliers} from './LinePlotExcludeOutliers';
import {LinePlotGroupingSetting} from './LinePlotGroupingSetting';
import {LinePlotMaxRuns} from './LinePlotMaxRuns';
import {LinePlotPointAggregation} from './LinePlotPointAggregation';
import {useLinePlotSettingsContext} from './LinePlotSettingsContextProvider';
import {LinePlotSinglePointRender} from './LinePlotSinglePointRender';
import {LinePlotSmoothing} from './LinePlotSmoothing';
import {LinePlotXAxis} from './LinePlotXAxis';
import {LinePlotYAxis} from './LinePlotYAxis';
import {DEFAULT_LINE_PLOT_NUM_RUNS_NEW} from './maxRunDefaults';
import {clampToLimit, getMaxRunsCeiling} from './utils/getCascadingMaxRuns';
import {useLinePlotSettingsWithDefaults} from './utils/getLinePlotSettings';

export const LinePlotDataTab = () => {
  const {isSingleRun} = useSingleRunModeContext();
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();

  const {
    clearSetting,
    displayLinePlotSettings,
    linePlotSettings,
    settingLevel,
    settingOverrideCounts,
    trackLinePlotSetting,
    updateLinePlotSettings,
  } = useLinePlotSettingsContext();

  const workspaceLinePlot = useLinePlotSettings();

  const {
    excludeOutliers,
    fullFidelityMode,
    limit,
    pointVisualizationMethod,
    smoothingType,
    smoothingWeight,
    xAxis,
    xAxisMax,
    xAxisMin,
    xLogScale,
    xAxisFormat,
    yAxisMax,
    yAxisMin,
    yLogScale,
    showOriginalAfterSmoothing,
  } = useLinePlotSettingsWithDefaults(displayLinePlotSettings);

  const isBucketing = isPointVizFullFidelity(pointVisualizationMethod);
  const {isAnyRunsetGrouped: isGrouped} = usePanelGroupingSettings();

  const maxRunsLimit = getMaxRunsCeiling({
    canOverrideMaxRuns,
    isBucketing: isPointVizFullFidelity(pointVisualizationMethod),
    dynamicConfigMaxRuns,
  });

  return (
    <div
      data-test="workspace-line-plot-data-settings"
      className={twMerge('flex flex-col', 'gap-20 py-16', 'h-full w-full')}>
      <LinePlotXAxis
        clearSetting={clearSetting}
        displayValues={{
          xAxis,
          xAxisMax,
          xAxisMin,
          xLogScale,
          xAxisFormat,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        trackSetting={trackLinePlotSetting}
        updateSetting={updateLinePlotSettings}
        values={{
          xAxis: linePlotSettings?.xAxis,
          // A null means "block inheritance." If the workspace value it was
          // blocking has been removed, the null is dangling; normalize it away.
          xAxisMax:
            settingLevel === 'section' &&
            linePlotSettings?.xAxisMax === null &&
            workspaceLinePlot.xAxisMax == null
              ? undefined
              : linePlotSettings?.xAxisMax,
          xAxisMin:
            settingLevel === 'section' &&
            linePlotSettings?.xAxisMin === null &&
            workspaceLinePlot.xAxisMin == null
              ? undefined
              : linePlotSettings?.xAxisMin,
          xLogScale: linePlotSettings?.xLogScale,
          xAxisFormat: linePlotSettings?.xAxisFormat,
        }}
      />
      <LinePlotYAxis
        clearSetting={clearSetting}
        displayValues={{
          yAxisMax,
          yAxisMin,
          yLogScale,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        trackSetting={trackLinePlotSetting}
        updateSetting={updateLinePlotSettings}
        values={{
          // Normalize dangling nulls — see xAxis comment above.
          yAxisMax:
            settingLevel === 'section' &&
            linePlotSettings?.yAxisMax === null &&
            workspaceLinePlot.yAxisMax == null
              ? undefined
              : linePlotSettings?.yAxisMax,
          yAxisMin:
            settingLevel === 'section' &&
            linePlotSettings?.yAxisMin === null &&
            workspaceLinePlot.yAxisMin == null
              ? undefined
              : linePlotSettings?.yAxisMin,
          yLogScale: linePlotSettings?.yLogScale,
        }}
      />
      <LinePlotSmoothing
        clearSetting={clearSetting}
        displayValues={{
          smoothingType,
          smoothingWeight,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        trackSetting={trackLinePlotSetting}
        updateSetting={updateLinePlotSettings}
        values={{
          smoothingType: linePlotSettings?.smoothingType,
          smoothingWeight: linePlotSettings?.smoothingWeight,
        }}
      />
      {smoothingWeight !== 0 && (isBucketing || !isGrouped) && (
        <LabeledOption
          label="Show Original"
          helpText={`Show lines before smoothing`}
          labelWidth="140px"
          option={
            <Tailwind>
              <Switch.Root
                size="medium"
                checked={showOriginalAfterSmoothing}
                onCheckedChange={isChecked => {
                  updateLinePlotSettings({
                    showOriginalAfterSmoothing: isChecked,
                  });
                }}>
                <Switch.Thumb
                  size="medium"
                  checked={showOriginalAfterSmoothing}
                />
              </Switch.Root>
            </Tailwind>
          }
        />
      )}
      <LinePlotExcludeOutliers
        clearSetting={clearSetting}
        disabled={
          isBucketing && fullFidelityMode === FULL_FIDELITY_MODES.MinMaxLast
        }
        displayValues={{
          excludeOutliers,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        updateSetting={updateLinePlotSettings}
        trackSetting={trackLinePlotSetting}
        values={{
          excludeOutliers: linePlotSettings?.excludeOutliers,
        }}
      />
      <LinePlotSinglePointRender />
      <LinePlotPointAggregation
        clearSetting={clearSetting}
        displayValues={{
          pointVisualizationMethod,
          fullFidelityMode,
        }}
        numOverrides={settingOverrideCounts}
        settingLevel={settingLevel}
        onPointVisualizationMethodChange={value => {
          updateLinePlotSettings({
            pointVisualizationMethod: value,
          });
          trackLinePlotSetting('pointVisualizationMethod', value.toString());
        }}
        onFullFidelityModeChange={value => {
          updateLinePlotSettings({
            fullFidelityMode: value,
          });
          trackLinePlotSetting('fullFidelityMode', value);
        }}
        values={{
          pointVisualizationMethod: linePlotSettings?.pointVisualizationMethod,
          fullFidelityMode: linePlotSettings?.fullFidelityMode,
        }}
      />

      {!isSingleRun && (
        <LinePlotMaxRuns
          clearSetting={clearSetting}
          displayValues={{
            maxRuns:
              clampToLimit(limit, maxRunsLimit) ??
              DEFAULT_LINE_PLOT_NUM_RUNS_NEW,
            pointVisualizationMethod,
          }}
          numOverrides={settingOverrideCounts}
          settingLevel={settingLevel}
          trackSetting={trackLinePlotSetting}
          updateSetting={updateLinePlotSettings}
          values={{
            maxRuns: linePlotSettings?.limit ?? linePlotSettings?.maxRuns,
          }}
        />
      )}
      <LinePlotGroupingSetting />
    </div>
  );
};
